import type { Workspace } from '../../types/workspace';
import type { RateLibraryItem } from '../../types/database';

export type TaxonomyType = 'nrm2' | 'smm7';

export interface WorkSectionLink {
  itemId: string;
  taxonomy: TaxonomyType;
  code: string;
  title: string;
  linkSource: 'direct' | 'via_smm7' | 'via_nrm2';
  /** Only populated for NRM2 rows that have an explicit sub-element
      assignment on the junction. SMM7 rows and unassigned NRM2 rows
      come through as null. */
  subElementId?: string | null;
  subElementName?: string | null;
  subElementSortOrder?: number | null;
  /** Display reference for the Ref column, e.g. "1.1". Stored on
      nrm2_sub_elements rather than derived from position, so it survives a
      re-sort. Null for SMM7 rows, whose code lives on the section itself. */
  subElementCode?: string | null;
  /** Optional 4th-level grouping inside a sub-element (e.g.
      Walls and partitions → Blockwork). NULL when the item should
      appear directly under the sub-element header. */
  granularBucketId?: string | null;
  granularBucketName?: string | null;
  granularBucketSortOrder?: number | null;
  /** Ref code, e.g. "14.1.1", extending the parent sub-element's. */
  granularBucketCode?: string | null;
  /** Optional 5th-level work-type grouping beneath a granular bucket.
      Example: Common bricks → Walls; in gauged mortar (1:1:6). */
  workGroupId?: string | null;
  workGroupName?: string | null;
  workGroupSortOrder?: number | null;
  /** Positional reference continuing the taxonomy spine, e.g. "14.2.4.1".
      Belongs to this placement, not to the item — an item in several sections
      has a different one in each. Null on SMM7 and cross-walked rows. */
  itemRef?: string | null;
}

export interface Nrm2Section {
  id: string;
  number: string;
  title: string;
}

export interface Smm7Section {
  id: string;
  code: string;
  title: string;
  parentCode: string | null;
  sortOrder: number;
}

export interface Nrm2SubElement {
  id: string;
  sectionNumber: number;
  name: string;
  keywords: string[];
  sortOrder: number;
}

export type ResourceCategory = 'Material' | 'Labour' | 'Plant' | 'Subcontractor';

export interface ResourceRow {
  /** Client-side stable id for React keys — never sent to the server. */
  _uid?: string;
  /** Id of the `rate_item_resources` row this line was loaded from. Absent on
   *  rows the client added, which is how a workspace save proves a line is a
   *  reprice of a Benchmark line rather than a new one. Identity fields can be
   *  edited in the master library, so they cannot carry that proof. */
  masterResourceId?: string;
  resourceDescription: string;
  category: ResourceCategory;
  constant: number;       // qty or hrs per output unit
  unit: string;
  wastePercentage: number;
  unitRate: number;       // £ per unit
  ohpPercentage: number;
  /** `productivity` keeps the source-book person-hours separate from the
   *  gang-hour constant used with a contractor's full gang rate. */
  inputMode?: 'consumption' | 'productivity';
  productivityValue?: number | null;
  productivityUnit?: string | null;
  gangHeadcount?: number | null;
  effectiveUnit?: string | null;
  isDerivedGangConstant?: boolean;
  sourcePricingBasis?: 'standard' | 'published_aggregate' | 'prime_cost_allowance' | null;
}

export interface ItemFormState {
  libraryId: string;
  libraryName?: string;
  itemId?: string;
}

export interface RateItemPageProps {
  libraryId: string;
  libraryName?: string;
  editingItemId?: string;
  initialItem?: RateLibraryItem;
  currentUserId: string;
  currentUserRole?: string;
  workspaceId?: string | null;
  /** True when the selected library is the master Quantis Benchmark library. */
  isMasterLibrary?: boolean;
  onBack: () => void;
  onSaved: () => void;
}

export interface ResourceLibraryManagementProps {
  currentUserId: string;
  currentUserRole: string;
  currentWorkspaceId?: string | null;
  availableWorkspaces?: Workspace[];
  /** Limits the catalogue list and creation controls to one workspace. Shared
   *  master/global/merchant catalogues remain available as reference sources. */
  workspaceScope?: { id: string; name: string } | null;
}

export const OPEN_WORKSPACE_RESOURCE_LIBRARY_EVENT = 'resource-library:open-workspace';

export interface OpenWorkspaceResourceLibraryDetail {
  workspaceId: string;
  workspaceName: string;
}
